// Mapping between the benefit application detail response and the bounded household
// endpoints. Household size, the five age brackets, and the disabled count never appear
// here: the server derives them from the member collection on every write, so the client
// only ever renders them.
import type { BenefitDetailDto, PersonDto } from "@/lib/benefit-types"

/** The legacy "not answered" sentinel every enumeration-backed byte uses. */
export const NOT_ANSWERED = 255

/**
 * `incorporated` is the one enumeration-backed field the API serializes as its display
 * string rather than a byte: `IncorporationStatus` is hand-written and the WinForms client
 * parses it that way. Reading it as a number therefore never matches.
 */
const incorporationByLabel: Record<string, number> = {
  Unincorporated: 0,
  Incorporated: 1,
  "": NOT_ANSWERED,
}

export function incorporationByte(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return NOT_ANSWERED
  if (typeof value === "number") return value
  return incorporationByLabel[value] ?? NOT_ANSWERED
}

export function byteOrNotAnswered(value: number | null | undefined): number {
  return value === null || value === undefined ? NOT_ANSWERED : value
}

export interface HouseholdInformationDraft {
  housingStatus: string
  housingType: string
  housingCost: string
  livedAtResidenceYears: string
  livedAtResidenceMonths: string
  familyType: string
  incorporated: string
  tribalMember: boolean
  militaryMemberInHousehold: boolean
}

export function householdInformationDraft(b: BenefitDetailDto): HouseholdInformationDraft {
  return {
    housingStatus: String(byteOrNotAnswered(b.housingStatus)),
    housingType: String(byteOrNotAnswered(b.housingType)),
    housingCost: String(b.housingCost ?? 0),
    livedAtResidenceYears: String(b.livedAtResidenceYears ?? 0),
    livedAtResidenceMonths: String(b.livedAtResidenceMonths ?? 0),
    // FamilyType is derived and persisted by the server from current identities. Do not
    // recalculate it in the browser with a second set of rules.
    familyType: String(byteOrNotAnswered(b.familyType)),
    incorporated: String(incorporationByte(b.incorporated)),
    tribalMember: b.tribalMember,
    militaryMemberInHousehold: b.militaryMemberInHousehold,
  }
}

export interface HouseholdInformationValidation {
  error: string | null
  request: Record<string, unknown> | null
}

export function householdInformationRequest(
  draft: HouseholdInformationDraft,
  concurrencyToken: string | null,
): HouseholdInformationValidation {
  const housingCost = Number(draft.housingCost === "" ? "0" : draft.housingCost)
  if (!Number.isFinite(housingCost) || housingCost < 0) {
    return { error: "Enter the monthly housing payment as a number of dollars.", request: null }
  }

  const years = Number(draft.livedAtResidenceYears === "" ? "0" : draft.livedAtResidenceYears)
  const months = Number(draft.livedAtResidenceMonths === "" ? "0" : draft.livedAtResidenceMonths)
  if (!Number.isInteger(years) || years < 0 || years > 120) {
    return { error: "Years at this residence must be between 0 and 120.", request: null }
  }

  // The server models the remainder of the duration, so 12 months is a year, not a value.
  if (!Number.isInteger(months) || months < 0 || months > 11) {
    return { error: "Months at this residence must be between 0 and 11.", request: null }
  }

  return {
    error: null,
    request: {
      concurrencyToken,
      housingStatus: Number(draft.housingStatus),
      housingType: Number(draft.housingType),
      housingCost,
      livedAtResidenceYears: years,
      livedAtResidenceMonths: months,
      familyType: Number(draft.familyType),
      incorporated: Number(draft.incorporated),
      tribalMember: draft.tribalMember,
      militaryMemberInHousehold: draft.militaryMemberInHousehold,
    },
  }
}

/** The legacy characteristic flag that marks a member disabled and feeds the derived count. */
export const DISABLED_CHARACTERISTIC = "D"

export function hasDisabilityFlag(characteristics: string | null | undefined): boolean {
  return (characteristics ?? "").toUpperCase().includes(DISABLED_CHARACTERISTIC)
}

export function setDisabilityFlag(characteristics: string | null | undefined, disabled: boolean): string {
  const rest = (characteristics ?? "").replace(/[Dd]/g, "")
  return disabled ? `${DISABLED_CHARACTERISTIC}${rest}` : rest
}

export interface HouseholdMemberDraft {
  lastName: string
  firstName: string
  middleName: string
  socialSecurityNumber: string
  birthDate: string
  gender: string
  relationship: string
  ethnicity: string
  sourceOfIncome: string
  workStatus: string
  medicalInsurance: string
  educationLevel: string
  eligibilityStatus: string
  otherCharacteristics: string
}

/** A member with nothing answered yet — the starting point for adding someone. */
export const emptyHouseholdMemberDraft: HouseholdMemberDraft = {
  lastName: "",
  firstName: "",
  middleName: "",
  socialSecurityNumber: "",
  birthDate: "",
  gender: String(NOT_ANSWERED),
  relationship: String(NOT_ANSWERED),
  ethnicity: String(NOT_ANSWERED),
  sourceOfIncome: String(NOT_ANSWERED),
  workStatus: String(NOT_ANSWERED),
  medicalInsurance: String(NOT_ANSWERED),
  educationLevel: String(NOT_ANSWERED),
  eligibilityStatus: String(NOT_ANSWERED),
  otherCharacteristics: "",
}

export function householdMemberDraft(person: PersonDto): HouseholdMemberDraft {
  return {
    lastName: person.lastName ?? "",
    firstName: person.firstName ?? "",
    middleName: person.middleName ?? "",
    socialSecurityNumber: person.socialSecurityNumber ?? "",
    birthDate: (person.birthDate ?? "").slice(0, 10),
    gender: String(byteOrNotAnswered(person.gender)),
    relationship: String(byteOrNotAnswered(person.relationship)),
    ethnicity: String(byteOrNotAnswered(person.ethnicity)),
    sourceOfIncome: String(byteOrNotAnswered(person.sourceOfIncome)),
    workStatus: String(byteOrNotAnswered(person.workStatus)),
    medicalInsurance: String(byteOrNotAnswered(person.medicalInsurance)),
    educationLevel: String(byteOrNotAnswered(person.educationLevel)),
    eligibilityStatus: String(byteOrNotAnswered(person.eligibilityStatus)),
    otherCharacteristics: person.otherCharacteristics ?? "",
  }
}

export function householdMemberDemographics(draft: HouseholdMemberDraft) {
  return {
    relationship: Number(draft.relationship),
    ethnicity: Number(draft.ethnicity),
    sourceOfIncome: Number(draft.sourceOfIncome),
    workStatus: Number(draft.workStatus),
    medicalInsurance: Number(draft.medicalInsurance),
    educationLevel: Number(draft.educationLevel),
    eligibilityStatus: Number(draft.eligibilityStatus),
    otherCharacteristics: draft.otherCharacteristics.trim(),
  }
}

export function householdMemberRequest(
  draft: HouseholdMemberDraft,
  concurrencyToken: string | null,
): { error: string | null; request: Record<string, unknown> | null } {
  if (!draft.lastName.trim() || !draft.firstName.trim() || !draft.birthDate) {
    return { error: "Last name, first name, and date of birth are required.", request: null }
  }

  const ssn = draft.socialSecurityNumber.replace(/\D/g, "")
  if (draft.socialSecurityNumber.trim() && ssn.length !== 9) {
    return {
      error: "Enter a complete 9-digit Social Security Number, or leave it blank.",
      request: null,
    }
  }

  return {
    error: null,
    request: {
      concurrencyToken,
      lastName: draft.lastName.trim(),
      firstName: draft.firstName.trim(),
      middleName: draft.middleName.trim(),
      socialSecurityNumber: ssn.length === 9 ? ssn : null,
      birthDate: draft.birthDate,
      gender: Number(draft.gender),
      demographics: householdMemberDemographics(draft),
    },
  }
}

/** Read the recorded calendar date, without moving it across a timezone boundary. */
function calendarDate(value: string | null | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value) || !Number.isFinite(Date.parse(value))) return null
  const day = value.slice(0, 10)
  const date = new Date(`${day}T00:00:00Z`)
  // Reject normalized dates such as February 30, and years outside DateOnly's range.
  return date.getUTCFullYear() > 0 && date.toISOString().slice(0, 10) === day ? date : null
}

/**
 * Completed years as of the recorded reference date. Matches HouseholdComposition.CompletedYears,
 * including DateOnly.AddYears treating February 28 as the anniversary of February 29 in non-leap years.
 * An absent/invalid reference never falls back to today or the intake date.
 */
export function completedYears(birthDate: string | null | undefined, asOf: string | null | undefined): number | null {
  const born = calendarDate(birthDate)
  const reference = calendarDate(asOf)
  if (!born || !reference || reference < born) return null
  const years = reference.getUTCFullYear() - born.getUTCFullYear()
  const anniversary = new Date(born)
  anniversary.setUTCFullYear(reference.getUTCFullYear())
  if (anniversary.getUTCMonth() !== born.getUTCMonth()) anniversary.setUTCDate(0)
  return years - (reference < anniversary ? 1 : 0)
}
